/**
 * Sector Share Modal Component
 *
 * Modal for sharing sector room links with QR code and copy functionality.
 * Shows command room connection info and generates shareable link.
 * Uses secure share data with encrypted room secrets.
 */

import { useState, useEffect, useMemo, useCallback } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import type { ExtendedSectorInfo } from '@/hooks/useHierarchy'
import { createSecureShareData } from '@/lib/crypto/room-encryption'

interface SectorShareModalProps {
  sector: ExtendedSectorInfo
  commandRoomId: string
  commandRoomName: string
  onClose: () => void
  baseUrl?: string
}

export function SectorShareModal({
  sector,
  commandRoomId,
  commandRoomName,
  onClose,
  baseUrl: _baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://crowdtalkie.com',
}: SectorShareModalProps) {
  void _baseUrl // May be used for custom base URL support
  const [linkCopied, setLinkCopied] = useState(false)
  const [secretCopied, setSecretCopied] = useState(false)
  const [showQR, setShowQR] = useState(true)

  // Generate secure share data
  const shareData = useMemo(() => {
    if (!sector.sectorSecret) {
      // Fallback for sectors without secrets (legacy)
      return null
    }
    return createSecureShareData(sector.roomId, sector.sectorSecret, sector.name)
  }, [sector.roomId, sector.sectorSecret, sector.name])

  // Add command room reference to the join URL
  const shareLink = useMemo(() => {
    if (!shareData) {
      return `${window.location.origin}/join/${sector.roomId}?cmd=${commandRoomId}`
    }
    const url = new URL(shareData.joinUrl)
    url.searchParams.set('cmd', commandRoomId)
    url.searchParams.set('hierarchyType', 'sector')
    return url.toString()
  }, [shareData, sector.roomId, commandRoomId])

  // QR data with command room reference
  const qrData = useMemo(() => {
    if (!shareData) {
      return JSON.stringify({
        roomId: sector.roomId,
        roomName: sector.name,
        commandRoomId,
        hierarchyType: 'sector',
      })
    }
    try {
      const parsed = JSON.parse(shareData.qrData)
      return JSON.stringify({
        ...parsed,
        commandRoomId,
        hierarchyType: 'sector',
      })
    } catch {
      return shareData.qrData
    }
  }, [shareData, sector.roomId, sector.name, commandRoomId])

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareLink)
      setLinkCopied(true)
      setTimeout(() => setLinkCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }, [shareLink])

  const handleCopySecret = useCallback(async () => {
    if (!shareData?.roomSecret) return
    try {
      await navigator.clipboard.writeText(shareData.roomSecret)
      setSecretCopied(true)
      setTimeout(() => setSecretCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }, [shareData])

  const handleDownloadQR = () => {
    const svg = document.getElementById('sector-qr-code')
    if (!svg) return

    // Convert SVG to PNG and download
    const svgData = new XMLSerializer().serializeToString(svg)
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    const img = new Image()

    img.onload = () => {
      canvas.width = 256
      canvas.height = 256
      ctx?.drawImage(img, 0, 0)
      const pngUrl = canvas.toDataURL('image/png')

      const link = document.createElement('a')
      link.download = `${sector.name.replace(/\s+/g, '-').toLowerCase()}-qr.png`
      link.href = pngUrl
      link.click()
    }

    img.src = 'data:image/svg+xml;base64,' + btoa(svgData)
  }

  // Close on escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [onClose])

  return (
    <div
      className="bg-gradient-to-b from-[#1a2028] to-[#0d1014] border border-teal/30 rounded-2xl max-w-md w-full shadow-[0_0_60px_rgba(27,127,143,0.2)] max-h-[90vh] flex flex-col overflow-hidden"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header - Sector Theme */}
      <div className="flex items-center justify-between p-4 bg-gradient-to-r from-teal/30 via-transparent to-teal/30 border-b border-teal/30 flex-shrink-0">
        <div>
          <h2 className="text-lg font-bold text-white flex items-center gap-2 tracking-wide">
            <span className="text-xl">🔗</span> Share Sector
          </h2>
          <p className="text-sm text-teal mt-0.5 font-medium font-mono">{sector.name}</p>
        </div>
        <button
          onClick={onClose}
          className="w-8 h-8 flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-all border border-white/10 hover:border-white/30"
        >
          ✕
        </button>
      </div>

        {/* Scrollable Content */}
        <div className="p-4 space-y-4 overflow-y-auto flex-1">
          {/* Command room info */}
          <div className="bg-white/5 rounded-lg p-3">
            <p className="text-xs text-white/60 uppercase tracking-wider mb-1">Under Command</p>
            <p className="text-sm text-primary font-medium">{commandRoomName}</p>
          </div>

          {/* QR Code */}
          {showQR && (
            <div className="flex flex-col items-center py-4">
              <div className="bg-white p-3 rounded-lg">
                <QRCodeSVG
                  id="sector-qr-code"
                  value={qrData}
                  size={160}
                  level="M"
                  includeMargin={false}
                  fgColor="#181111"
                  bgColor="#ffffff"
                />
              </div>
              <p className="text-xs text-white/40 mt-2">Scan to join sector</p>
            </div>
          )}

          {/* Toggle QR */}
          <button
            onClick={() => setShowQR(!showQR)}
            className="w-full text-xs text-white/60 hover:text-white py-1 transition-colors"
          >
            {showQR ? 'Hide QR Code' : 'Show QR Code'}
          </button>

          {/* Share link */}
          <div className="space-y-2">
            <label className="text-xs text-white/60 uppercase tracking-wider font-bold">Share Link</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={shareLink}
                readOnly
                className="flex-1 bg-secondary/50 text-white/80 text-xs rounded-lg p-2
                  border border-white/10 focus:outline-none font-mono"
              />
              <button
                onClick={handleCopyLink}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors
                  ${linkCopied
                    ? 'bg-green-500 text-white'
                    : 'bg-primary text-white hover:bg-primary/90'
                  }`}
              >
                {linkCopied ? '✓' : '📋'}
              </button>
            </div>
          </div>

          {/* Room Secret */}
          {shareData?.roomSecret && (
            <div className="space-y-2">
              <label className="text-xs text-yellow-400 uppercase tracking-wider font-bold">
                Room Secret (share separately)
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={shareData.roomSecret.substring(0, 12) + '...'}
                  readOnly
                  className="flex-1 bg-yellow-500/10 text-yellow-200/80 text-xs rounded-lg p-2
                    border border-yellow-500/30 focus:outline-none font-mono"
                />
                <button
                  onClick={handleCopySecret}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors
                    ${secretCopied
                      ? 'bg-green-500 text-white'
                      : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 hover:bg-yellow-500/30'
                    }`}
                >
                  {secretCopied ? '✓' : '🔐'}
                </button>
              </div>
              <p className="text-[10px] text-yellow-400/60 flex items-center gap-1">
                <span>⚠</span> Send via secure channel (Signal, in-person, etc.)
              </p>
            </div>
          )}

          {/* No secret warning */}
          {!shareData?.roomSecret && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-xs text-red-400">
              <p className="font-medium flex items-center gap-1">
                <span>⚠️</span> No sector secret available
              </p>
              <p className="text-red-400/70 mt-1">
                This sector was created without a secret. Delete and recreate it to enable secure sharing.
              </p>
            </div>
          )}

          {/* Sector info */}
          <div className="bg-white/5 rounded-lg p-3 text-sm">
            <div className="grid grid-cols-2 gap-2 text-white/60">
              <div>
                <span className="text-white/40">Status:</span>{' '}
                <span className="capitalize">{sector.status}</span>
              </div>
              <div>
                <span className="text-white/40">Participants:</span>{' '}
                {sector.participantCount}
              </div>
              {sector.radiusMeters && (
                <div>
                  <span className="text-white/40">Radius:</span>{' '}
                  {sector.radiusMeters}m
                </div>
              )}
              {sector.leadName && (
                <div>
                  <span className="text-white/40">Lead:</span>{' '}
                  {sector.leadName}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-2 p-4 border-t border-teal/20 flex-shrink-0 bg-black/30">
          <button
            onClick={handleDownloadQR}
            className="flex-1 py-3 bg-white/10 text-white/80 rounded-xl font-bold uppercase tracking-wider
              hover:bg-white/20 transition-all text-sm border border-white/20"
          >
            📥 Download QR
          </button>
          <button
            onClick={onClose}
            className="flex-1 py-3 bg-gradient-to-r from-teal to-teal/80 text-white rounded-xl font-bold uppercase tracking-wider
              hover:from-teal/90 hover:to-teal/70 transition-all text-sm shadow-[0_0_20px_rgba(27,127,143,0.3)]"
          >
            Done
          </button>
        </div>
      </div>
  )
}

export default SectorShareModal
